/**
 * Tags Service
 *
 * API endpoints for tag operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';

/**
 * Tag type
 */
export interface Tag {
  tag_id: number;
  tag_name: string;
  created_at?: string;
}

/**
 * Tag with usage count
 */
export interface TagWithCount extends Tag {
  investment_count: number;
}

/**
 * Investment tag (tag assigned to an investment)
 */
export interface InvestmentTag {
  investment_tag_id: number;
  tag_id: number;
  investment_id: number;
  tag_name: string;
  created_at?: string;
}

/**
 * Add tag request DTO
 */
export interface AddTagDto {
  tag_id?: number;
  tag_name?: string;
}

/**
 * Add tag response
 */
export interface AddTagResponse {
  investment_tag_id: number;
  investment_id: number;
  tag_id: number;
  tag_name: string;
  is_new_tag: boolean;
}

/**
 * Remove tag response
 */
export interface RemoveTagResponse {
  message: string;
  investment_id: number;
  tag_id: number;
  tag_name: string;
  tag_deleted: boolean;
}

/**
 * Tags list response
 */
export interface TagsListResponse {
  tags: TagWithCount[];
  total: number;
}

/**
 * Investment tags list response
 */
export interface InvestmentTagsResponse {
  investment_id: number;
  tags: InvestmentTag[];
  total: number;
}

/**
 * Tags Service - Global tags operations
 */
class TagsService {
  /**
   * Get all tags with usage counts
   */
  async getAll(search?: string): Promise<ApiResponse<TagsListResponse>> {
    const params: Record<string, string> = {};
    if (search) {
      params.search = search;
    }
    return apiClient.get<TagsListResponse>('/tags', { params });
  }
}

/**
 * Investment Tags Service - Tag operations for specific investments
 */
class InvestmentTagsService {
  /**
   * Get all tags for an investment
   */
  async getByInvestmentId(investmentId: number): Promise<ApiResponse<InvestmentTagsResponse>> {
    return apiClient.get<InvestmentTagsResponse>(`/investments/${investmentId}/tags`);
  }

  /**
   * Add a tag to an investment
   * Can provide either tag_id (existing) or tag_name (creates if new)
   */
  async addTag(investmentId: number, data: AddTagDto): Promise<ApiResponse<AddTagResponse>> {
    return apiClient.post<AddTagResponse>(`/investments/${investmentId}/tags`, data);
  }

  /**
   * Remove a tag from an investment
   */
  async removeTag(investmentId: number, tagId: number): Promise<ApiResponse<RemoveTagResponse>> {
    return apiClient.delete<RemoveTagResponse>(`/investments/${investmentId}/tags/${tagId}`);
  }
}

export const tagsService = new TagsService();
export const investmentTagsService = new InvestmentTagsService();
